/**
 * Prestige dimension steps and external-depth ↔ internal-depth mapping.
 * Depth external range extends below 3 m so the minimum shown depth can start at 8'.
 */

const TWO_INCH_CM = 2 * 2.54;
const EIGHT_FT_CM = 8 * 12 * 2.54;
const DEPTH_EXTERNAL_MAX_CM = 400.6;
const HEIGHT_EXTERNAL_MAX_CM = (9 * 12 + 10) * 2.54;

/** [internal depth m, external depth cm] — matches legacy table from 3 m upward; extended downward in 0.15 m / 15.1 cm steps */
const DEPTH_KNOTS: Array<[number, number]> = [
  [2.25, 233.6],
  [2.4, 248.7],
  [2.55, 263.8],
  [2.7, 278.9],
  [2.85, 294.0],
  [3, 309.1],
  [3.15, 324.2],
  [3.3, 339.3],
  [3.45, 354.4],
  [3.6, 370.0],
  [3.75, 385.1],
  [4, 400.6],
];

const roundM = (value: number): number => Math.round(value * 1e6) / 1e6;

/**
 * Internal depth (m) → exact external depth (cm), unrounded. Engineered
 * drawings use this so 2" steps land exactly on 1/64" values.
 */
export const exactExternalDepthCmFromDepthM = (depthM: number): number => {
  const knots = DEPTH_KNOTS;
  if (depthM <= knots[0][0]) {
    const [d0, c0] = knots[0];
    const [d1, c1] = knots[1];
    return c0 + (depthM - d0) * (c1 - c0) / (d1 - d0);
  }
  if (depthM >= knots[knots.length - 1][0]) {
    const [d0, c0] = knots[knots.length - 2];
    const [d1, c1] = knots[knots.length - 1];
    return c0 + (depthM - d0) * (c1 - c0) / (d1 - d0);
  }
  for (let i = 0; i < knots.length - 1; i += 1) {
    const [d0, c0] = knots[i];
    const [d1, c1] = knots[i + 1];
    if (depthM >= d0 && depthM <= d1) {
      const t = (depthM - d0) / (d1 - d0);
      return c0 + t * (c1 - c0);
    }
  }
  return depthM * 100;
};

/** Internal depth (m) → external depth (cm) for UI and persistence */
export const externalDepthCmFromDepthM = (depthM: number): number =>
  Math.round(exactExternalDepthCmFromDepthM(depthM) * 10) / 10;

/** External depth target (cm) → internal depth (m) */
export const depthMFromExternalDepthCm = (externalCm: number): number => {
  const knots = DEPTH_KNOTS;
  if (externalCm <= knots[0][1]) {
    const [d0, c0] = knots[0];
    const [d1, c1] = knots[1];
    return roundM(d0 + (externalCm - c0) * (d1 - d0) / (c1 - c0));
  }
  if (externalCm >= knots[knots.length - 1][1]) {
    const [d0, c0] = knots[knots.length - 2];
    const [d1, c1] = knots[knots.length - 1];
    return roundM(d0 + (externalCm - c0) * (d1 - d0) / (c1 - c0));
  }
  for (let i = 0; i < knots.length - 1; i += 1) {
    const [d0, c0] = knots[i];
    const [d1, c1] = knots[i + 1];
    if (externalCm >= c0 && externalCm <= c1) {
      const t = (externalCm - c0) / (c1 - c0);
      return roundM(d0 + t * (d1 - d0));
    }
  }
  return roundM(externalCm / 100);
};

const buildDepthSteps = (): number[] => {
  const steps: number[] = [];
  for (let cm = EIGHT_FT_CM; cm <= DEPTH_EXTERNAL_MAX_CM + 1e-6; cm += TWO_INCH_CM) {
    steps.push(depthMFromExternalDepthCm(cm));
  }
  const atMax = depthMFromExternalDepthCm(DEPTH_EXTERNAL_MAX_CM);
  if (steps.length === 0 || Math.abs(steps[steps.length - 1] - atMax) > 1e-4) {
    steps.push(atMax);
  }
  return steps;
};

const buildHeightSteps = (): number[] => {
  const steps: number[] = [];
  for (let cm = EIGHT_FT_CM; cm <= HEIGHT_EXTERNAL_MAX_CM + 1e-6; cm += TWO_INCH_CM) {
    steps.push(roundM((cm + 3.4) / 100));
  }
  const atMax = roundM((HEIGHT_EXTERNAL_MAX_CM + 3.4) / 100);
  if (steps.length === 0 || Math.abs(steps[steps.length - 1] - atMax) > 1e-4) {
    steps.push(atMax);
  }
  return steps;
};

/**
 * External pergola widths in cm, taken verbatim from the
 * "Pergola - Price" sheet row 4 (4-leg + 6-leg sections concatenated).
 *
 * `widthValue` maps 1:1 to this array via `widthValue = PRESTIGE_WIDTH_VALUE_MIN + index`.
 * Length: 20 one-room widths followed by 41 two-room widths = 61 total.
 *
 * Indices 0..19  → one-room  (widthValue 19..38)
 * Indices 20..60 → two-room  (widthValue 39..79)
 *
 * In 2-room mode the total louver count = widthValue − 1, split between
 * rooms as ceil(total/2) + floor(total/2). Even-positioned widthValues
 * yield symmetric rooms; odd-positioned ones yield asymmetric rooms
 * (one room has 1 more louver than the other).
 */
export const PRESTIGE_EXTERNAL_WIDTHS_CM: readonly number[] = [
  // 4-leg / 1-room (20 widths) — sheet row 4 cols C..V
  309.1, 324.2, 339.3, 354.4, 369.5, 384.6, 399.7, 414.8, 429.9, 445.0,
  460.1, 475.2, 490.3, 505.4, 520.5, 535.6, 550.7, 565.8, 580.9, 596.0,
  // 6-leg / 2-room (41 widths) — sheet row 4 cols W..BK
  603.2, 618.3, 633.4, 648.5, 663.6, 678.7, 693.8, 708.9, 724.0,
  739.1, 754.2, 769.3, 784.4, 798.45, 813.21, 827.97, 842.72, 857.48,
  872.24, 886.99, 901.75, 916.51, 931.26, 946.02, 960.78, 975.53,
  990.29, 1005.05, 1019.8, 1034.56, 1049.32, 1064.07, 1078.83, 1093.59,
  1108.34, 1123.1, 1137.86, 1152.61, 1167.37, 1182.13, 1196.88,
];

export const PRESTIGE_WIDTH_VALUE_MIN = 19;
export const PRESTIGE_WIDTH_VALUE_MAX =
  PRESTIGE_WIDTH_VALUE_MIN + PRESTIGE_EXTERNAL_WIDTHS_CM.length - 1;

/** widthValue at which the configurator transitions from 1-room to 2-room. */
export const PRESTIGE_TWO_ROOM_WIDTH_VALUE_MIN = 39;

const ONE_ROOM_WIDTH_COUNT = PRESTIGE_TWO_ROOM_WIDTH_VALUE_MIN -
  PRESTIGE_WIDTH_VALUE_MIN;

/** Two-room slice of `PRESTIGE_EXTERNAL_WIDTHS_CM` (sheet 6-leg section). */
export const PRESTIGE_TWO_ROOM_EXTERNAL_CM: readonly number[] =
  PRESTIGE_EXTERNAL_WIDTHS_CM.slice(ONE_ROOM_WIDTH_COUNT);

const buildWidthValues = (): number[] => {
  const values: number[] = [];
  for (
    let v = PRESTIGE_WIDTH_VALUE_MIN;
    v <= PRESTIGE_WIDTH_VALUE_MAX;
    v += 1
  ) {
    values.push(v);
  }
  return values;
};

export const PRESTIGE_DIMENSIONS: {
  width: number[];
  depth: number[];
  height: number[];
} = {
  width: buildWidthValues(),
  depth: buildDepthSteps(),
  height: buildHeightSteps(),
};
